import { useCallback, useContext, useEffect, useMemo, useRef, useState, type CSSProperties, type MutableRefObject } from "react";
import { useAtomValue } from "jotai";
import dayjs from "dayjs";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { ChartShell, type ChartShellHandle, type ChartLayout, type OverlayDef } from "@ortex-charts/react";
import type { ToolbarAction } from "@ortex-charts/ui";
import type { ChartEvent } from "@ortex-charts/financial";
import type { SeriesModel } from "@ortex-charts/core";
import { Context } from "@/lib/Context";
import { greyState, textColorState, userState } from "@/lib/store";
import { Stock } from "@/models/stock";
import { getChartDataCustomSeries, getNonStockEndpoints, getStockEndpoints } from "@/controllers/chart-data";
import { getStockInfoOnClient } from "@/controllers/stock";
import { getStockShortsAPIs2, getStockShortsFlagsData } from "@/controllers/stock-shorts";
import { getStockEvents } from "@/controllers/stock-events";
import type { ChartAPI, ChartLegendResponse } from "@/models/stock-shorts";
import { atExchangeMidnight, createOrtexDatafeed, ORTEX_RESOLUTIONS } from "@/lib/ortex-charts/datafeed";
import { ensureOrtexCharts } from "@/lib/ortex-charts/setup";
import { createBarStore, firstBarsWindow, type BarStore } from "@/lib/ortex-charts/barStore";
import { useRouter } from "next/router";
import { useOrtexChartTheme } from "@/lib/ortex-charts/theme";
import { SHAREABLE_MEASURES, createSharedChart, measureKeyFromUrl } from "@/controllers/shared-charts";
import { UPGRADE_LOCATIONS, trackMIEvent } from "@/lib/tracking";
import { getPreference, listPreferences, savePreference, useChartPreference } from "@/lib/ortex-charts/preferences";
import SnackBar from "@/components/snack-bar/SnackBar";
import styles from "./StockChart.module.scss";
import useUpgradeCta from "src/hooks/useUpgradeCta";

/**
 * The stock chart of the app, on ORTEX Charts: price with volume, the ORTEX
 * data series as overlays the user turns on and off, company events on the
 * timeline, indicators and drawings from the library, and the user's choice
 * of lines and scales saved per kind of page (see lib/ortex-charts/preferences).
 *
 * Two modes share one component:
 *  - `overview`: the stock page chart, with every series the ts_endpoints
 *    catalogue offers for the instrument (and the generic ones), grouped.
 *  - `shorts`: the short-interest chart, with the short-interest legend
 *    (groups, axes, flags) and the Advanced set for the tiers that have it.
 */
export type StockChartMode = "overview" | "shorts";

export interface StockChartProps {
    stock?: Stock;
    /** `EXCHANGE:TICKER` when there is no stock object. */
    symbol?: string;
    mode?: StockChartMode;
    /** Distinguishes charts on the same kind of page in the saved preferences. */
    chartId?: string;
    /** Short-interest legend for official reports rather than estimates. */
    officialReports?: boolean;
    height?: number | string;
    className?: string;
    style?: CSSProperties;
    /** Called once the shell exists. */
    onReady?: (shell: ChartShellHandle) => void;
    /** Set to a function that opens the share flow, for a header icon outside the chart. */
    shareHandlerRef?: MutableRefObject<(() => void) | null>;
}

interface Endpoint {
    id?: string;
    url: string;
    dataName: string;
    dataKey: string;
    axis?: string;
    sortKey?: number;
    color?: string | number;
    fieldDefinitions?: string[];
    visible?: boolean;
    plotType?: string;
    millify?: boolean;
    yAxisType?: number;
    zIndex?: number;
    groups?: string[];
    tooltip?: string;
    axisDoNotAdd?: boolean;
    section?: string;
}

/** Event-marker pseudo-series in the short-interest legend: fetched as `[time, message]` rows, drawn as badges. */
const FLAG_KEYS: Record<string, { label: string; up: boolean }> = {
    dividend: { label: "Dividend", up: true },
    lockup: { label: "Lock-up expiry", up: false },
    offering: { label: "Offering", up: false },
    buyback: { label: "Buyback", up: true },
    index_change: { label: "Index change", up: true },
    split: { label: "Split", up: true },
};

/**
 * Is this catalogue row drawn as markers rather than as a line?
 *
 * Two catalogues answer, and both have to be read. The short-interest legend
 * names its flag series by key, which is the map above. The overview catalogue
 * marks the company-event families -- earnings, offerings, roadshows and the
 * rest -- with `axis: "flags"` and a `event_<family>` key, which is web's own
 * discriminator (`domain/chart/data_availability.py`). Reading only the key
 * list sent those rows down the numeric path, where a `[date, [label, text]]`
 * row has no number in it: the request succeeded, every row was discarded, and
 * the chart drew nothing.
 */
function flagOf(e: Endpoint): { label: string; up: boolean } | null {
    const known = FLAG_KEYS[e.dataKey];
    if (known) return known;
    if (e.axis === "flags" || e.dataKey?.startsWith("event_")) return { label: e.dataName || "Event", up: true };
    return null;
}

const ADVANCED_PLANS = ["ORTEX Advanced", "ORTEX Institutional"];
const PALETTE = ["#4C8DFF", "#F0B429", "#B36AE2", "#3DBFD9", "#FF8A3D", "#8FD14F", "#E06AA8", "#7cb5ec", "#90ed7d", "#f7a35c"];

/**
 * One request per (series, from, to): the platform answers by whole days, so
 * a loader asking again for the same days must get the same answer without
 * another round trip, empty ones included. Each entry is a whole overlay
 * window, so the cache is small and short-lived: a reader comparing overlays
 * on one chart is served; a tab left open all day is not asked to hold them.
 */
const SERIES_WINDOW_MS = 10 * 60_000;
const SERIES_WINDOWS_MAX = 50;
const seriesWindows = new Map<string, { at: number; p: Promise<Array<{ time: number; value: number }>> }>();
function fetchSeriesWindow(request: string, fromDate: string, toDate: string): Promise<Array<{ time: number; value: number }>> {
    const key = `${request}|${fromDate}|${toDate}`;
    const now = Date.now();
    const hit = seriesWindows.get(key);
    if (hit && now - hit.at <= SERIES_WINDOW_MS) return hit.p;
    seriesWindows.forEach((v, k) => now - v.at > SERIES_WINDOW_MS && seriesWindows.delete(k));
    const p = getChartDataCustomSeries(request, fromDate, toDate)
        .then((raw) => toPoints(raw) as Array<{ time: number; value: number }>)
        .catch(() => [] as Array<{ time: number; value: number }>);
    seriesWindows.set(key, { at: now, p });
    if (seriesWindows.size > SERIES_WINDOWS_MAX) seriesWindows.delete(seriesWindows.keys().next().value as string);
    return p;
}

const symbolOf = (stock?: Stock, symbol?: string) => (stock ? `${stock.exchangesymbol}:${stock.ticker}` : (symbol ?? ""));

function overlayId(e: Endpoint): string {
    return `${e.dataKey}|${e.dataName}`;
}

/**
 * API rows as chart points. A row with a time but no number (the obfuscated tail is
 * `[time, "undefined"]`) becomes a `NaN` gap rather than being dropped: a gap stops the
 * line at the last real point, whereas a dropped row lets `forwardFill` carry that point
 * flat to the newest bar.
 */
function toPoints(raw: unknown): { time: number; value: number }[] {
    const rows = Array.isArray(raw) ? raw : Array.isArray((raw as { data?: unknown })?.data) ? (raw as { data: unknown[] }).data : [];
    return (rows as unknown[])
        .filter((r): r is [number, unknown] => Array.isArray(r) && r.length >= 2 && Number.isFinite(r[0]))
        .map((r) => {
            const v = r[1] == null || r[1] === "" ? NaN : Number(r[1]);
            return { time: r[0], value: Number.isFinite(v) ? v : NaN };
        });
}

/** The URL's own tradingitem/security id swapped for another instrument's, so an overlay follows a symbol change. */
function retarget(url: string, from: number | null, to: number | null): string {
    if (!from || !to || from === to) return url;
    return url.replace(new RegExp(`/${from}(/|$|\\?)`), `/${to}$1`);
}

type StockChartPref = {
    v?: number;
    layout: ChartLayout | null;
    volume: boolean;
};

/** A floppy disk, 20×20 like the shell's own icons. */
const SAVE_ICON = '<svg viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 3h9l3 3v11H4z"/><path d="M7 3v4h5V3M7 17v-5h6v5"/></svg>';

/**
 * Unsaved chart changes, per page kind, for as long as the tab lives: what the
 * user sees follows them from stock to stock until they save or reset it.
 */
const sessionDrafts = new Map<string, StockChartPref>();

/** Named templates live under one key prefix, shared by every stock page kind. */
const TEMPLATE_PREFIX = "/templates/chart#";
type ChartTemplate = StockChartPref & { name: string };

/** Bump when a saved document must be discarded (v1 and v2 layouts could carry every catalogue series). */
const PREF_VERSION = 3;
/** A restored layout with more overlays than anyone would pick by hand is a corrupt document, not a preference. */
const MAX_RESTORED_OVERLAYS = 40;
// No `v` here: a stored document from before the schema version lacks it, and the fallback must not lend it one.
const PREF_FALLBACK: StockChartPref = { layout: null, volume: true };
/** Bars the shell asks for on a symbol's first load; the page's early bar request is sized from it too. */
const INITIAL_BARS = 400;
const DAY_MS = 86_400_000;

const StockChart = ({ stock, symbol: symbolProp, mode = "overview", chartId, officialReports, height, className, style, onReady, shareHandlerRef }: StockChartProps) => {
    const { ortexWebSocket } = useContext(Context);
    const user = useAtomValue(userState);
    const grey = useAtomValue(greyState);
    const textColor = useAtomValue(textColorState);
    const theme = useOrtexChartTheme();
    // On a stock page the page itself chooses the instrument; the chart's own picker only belongs elsewhere.
    const onStockPage = (useRouter()?.pathname ?? "").startsWith("/s/");
    const symbol = symbolOf(stock, symbolProp);
    const advanced = ADVANCED_PLANS.includes(user?.subscription_plan);
    const shellRef = useRef<ChartShellHandle | null>(null);
    /**
     * The series catalogue, tagged with the symbol it was fetched for.
     *
     * The shell mounts only once the catalogue is for the symbol on screen. A
     * separate "ready" flag lagged one render behind: on a change of stock the
     * shell remounted with the old stock's catalogue, was torn down a render
     * later, and on its way out reported a layout with no lines in it, which
     * the session draft then handed to the real shell. Every navigation from
     * one stock to the next lost the short-interest lines that way.
     */
    const [catalogue, setCatalogue] = useState<{ symbol: string; endpoints: Endpoint[]; groups: Record<string, string>; legend: ChartLegendResponse | null } | null>(null);
    const ready = !!symbol && catalogue?.symbol === symbol;
    const [helpOpen, setHelpOpen] = useState(false);
    const [snack, setSnack] = useState<{ open: boolean; text: string; severity: "success" | "error" | "info" | "warning"; action?: { label: string; onClick: () => void } }>({ open: false, text: "", severity: "success" });
    const shareSignup = useUpgradeCta(UPGRADE_LOCATIONS.CHART_SHARE_SIGNUP, { ticker: stock?.ticker });
    const shortsUnlock = useUpgradeCta(UPGRADE_LOCATIONS.STOCK_CHART_SHORT_INTEREST, { ticker: stock?.ticker });
    const [rawPref, , resetPageDefault, prefMeta] = useChartPreference<StockChartPref>(chartId ?? mode, PREF_FALLBACK);
    // A saved chart for this stock alone sits beside the default for every page of this kind.
    const exactKey = `${(useRouter()?.asPath ?? "/").split(/[?#]/)[0]}#${chartId ?? mode}`;
    const [, bumpDraft] = useState(0);
    // A document from before the current schema is ignored and replaced on the next save rather than merged into.
    const usable = (doc?: StockChartPref | null) => (doc && doc.v === PREF_VERSION && (doc.layout?.overlays?.length ?? 0) <= MAX_RESTORED_OVERLAYS ? doc : undefined);
    const pref = sessionDrafts.get(prefMeta.key) ?? usable(getPreference(exactKey) as StockChartPref | undefined) ?? usable(rawPref) ?? PREF_FALLBACK;
    const prefRef = useRef(pref);
    prefRef.current = pref;
    /** Changes the user makes stay with them while they browse; only Save writes them down. */
    const setPref = useCallback(
        (patch: Partial<StockChartPref>) => {
            sessionDrafts.set(prefMeta.key, { ...prefRef.current, v: PREF_VERSION, ...patch });
            bumpDraft((n) => n + 1);
        },
        [prefMeta.key]
    );
    const pageKind = mode === "shorts" ? "short-interest" : mode === "overview" ? "overview" : "this kind of";
    /** The chart as it is on screen, ready to be written down. */
    const currentDoc = useCallback((shell: ChartShellHandle | null): StockChartPref => ({ v: PREF_VERSION, layout: shell ? shell.getLayout() : prefRef.current.layout, volume: prefRef.current.volume !== false }), []);
    const saveForStock = useCallback(
        (shell: ChartShellHandle | null) => {
            savePreference(exactKey, currentDoc(shell));
            sessionDrafts.delete(prefMeta.key);
            setSnack({ open: true, text: `Chart saved for ${symbol}.`, severity: "success" });
        },
        [exactKey, currentDoc, prefMeta.key, symbol]
    );
    const saveAsDefault = useCallback(
        (shell: ChartShellHandle | null) => {
            savePreference(prefMeta.key, currentDoc(shell));
            // One source of truth: a chart saved for this stock alone would otherwise hide the new default here.
            savePreference(exactKey, null);
            sessionDrafts.delete(prefMeta.key);
            setSnack({ open: true, text: `This chart is now your default on every ${pageKind} page.`, severity: "success" });
        },
        [prefMeta.key, exactKey, currentDoc, pageKind]
    );
    const resetPref = useCallback(() => {
        resetPageDefault();
        savePreference(exactKey, null);
        sessionDrafts.delete(prefMeta.key);
        bumpDraft((n) => n + 1);
    }, [resetPageDefault, exactKey, prefMeta.key]);
    const [templateDialog, setTemplateDialog] = useState<{ open: boolean; name: string; mode: "save" | "load" }>({ open: false, name: "", mode: "save" });
    // Cheap enough to read on every render, and it follows every save, delete and the initial load.
    const templates = listPreferences(TEMPLATE_PREFIX)
        .map(([key, doc]) => ({ key, ...(doc as ChartTemplate) }))
        .filter((t) => t.name)
        .sort((a, b) => a.name.localeCompare(b.name));
    const saveTemplate = useCallback(
        (name: string) => {
            const trimmed = name.trim();
            if (!trimmed) return;
            savePreference(`${TEMPLATE_PREFIX}${encodeURIComponent(trimmed).slice(0, 80)}`, { ...currentDoc(shellRef.current), name: trimmed });
            setTemplateDialog({ open: false, name: "", mode: "save" });
            setSnack({ open: true, text: `Template "${trimmed}" saved.`, severity: "success" });
        },
        [currentDoc]
    );

    // Per-symbol instrument ids and catalogues, so an overlay can follow a symbol change.
    const idsRef = useRef<Map<string, { tradingitemId: number | null; securityId: number | null; companyId: number | null }>>(new Map());
    const catalogueRef = useRef<Map<string, Endpoint[]>>(new Map());
    const eventsRef = useRef<Map<string, ChartEvent[]>>(new Map());

    // ADJ toggle: read by the datafeed on every bar request.
    const adjustedRef = useRef(false);
    const [adjusted, setAdjusted] = useState(false);
    // One bar store for every datafeed this chart creates: the datafeed is made again when the socket
    // arrives, and the early bar request below must still be there for the shell's first request.
    const [barStore] = useState<BarStore>(() => createBarStore());
    const datafeed = useMemo(
        () =>
            createOrtexDatafeed({
                socket: ortexWebSocket ?? null,
                adjusted: () => adjustedRef.current,
                // The page already has this stock; without it the chart's first
                // act is to ask the API for what is on the screen behind it.
                seed: stock && symbol ? { symbol, stock: stock as unknown as Record<string, unknown> } : null,
                bars: barStore,
                onResolved: (info, tradingitemId, raw) => {
                    idsRef.current.set(info.symbol, { tradingitemId, securityId: (raw.security_id as number) ?? null, companyId: (raw.company_id as number) ?? null });
                },
            }),
        [ortexWebSocket]
    );
    const datafeedRef = useRef(datafeed);
    datafeedRef.current = datafeed;
    const prefLoadedRef = useRef(prefMeta.loaded);
    prefLoadedRef.current = prefMeta.loaded;

    const idsFor = useCallback(
        async (sym: string) => {
            const hit = idsRef.current.get(sym);
            if (hit) return hit;
            if (stock && sym === symbol) {
                const ids = { tradingitemId: stock.tradingitem_id ?? null, securityId: stock.security_id ?? null, companyId: stock.company_id ?? null };
                idsRef.current.set(sym, ids);
                return ids;
            }
            const [exchange, ticker] = sym.split(":");
            const info = (await getStockInfoOnClient(exchange, ticker)) as unknown as Record<string, number | null>;
            const ids = { tradingitemId: info.tradingitem_id ?? null, securityId: info.security_id ?? null, companyId: info.company_id ?? null };
            idsRef.current.set(sym, ids);
            return ids;
        },
        [stock, symbol]
    );

    /** The series catalogue for a symbol: the short-interest legend, or the ts_endpoints sections. */
    const catalogueFor = useCallback(
        async (sym: string): Promise<{ endpoints: Endpoint[]; groups: Record<string, string>; legend: ChartLegendResponse | null }> => {
            const cached = catalogueRef.current.get(sym);
            const ids = await idsFor(sym);
            if (cached) return { endpoints: cached, groups: {}, legend: null };
            let endpoints: Endpoint[] = [];
            const groups: Record<string, string> = {};
            let legend: ChartLegendResponse | null = null;
            if (mode === "shorts") {
                if (ids.tradingitemId) {
                    legend = await getStockShortsAPIs2(ids.tradingitemId, advanced, officialReports ? { chart_type: "official_report" } : {});
                    legend.groups?.forEach((g) => (groups[g.key] = g.displayName));
                    endpoints = (legend.apis ?? []).map((api: ChartAPI) => ({ ...api, section: api.groups?.[0] ? (groups[api.groups[0]] ?? api.groups[0]) : "Short interest" }));
                }
            } else {
                const [own, generic] = await Promise.all([ids.tradingitemId ? getStockEndpoints(String(ids.tradingitemId)).catch(() => ({})) : Promise.resolve({}), getNonStockEndpoints().catch(() => ({}))]);
                for (const [section, list] of Object.entries({ ...(own as Record<string, Endpoint[]>), ...(generic as Record<string, Endpoint[]>) })) {
                    if (!Array.isArray(list)) continue;
                    list.forEach((e) => endpoints.push({ ...e, section }));
                }
            }
            catalogueRef.current.set(sym, endpoints);
            return { endpoints, groups, legend };
        },
        [mode, advanced, officialReports, idsFor]
    );

    useEffect(() => {
        let alive = true;
        void ensureOrtexCharts()
            .then(() => (symbol ? catalogueFor(symbol) : Promise.resolve({ endpoints: [], groups: {}, legend: null })))
            .then((c) => {
                if (alive) setCatalogue({ symbol, ...c });
            });
        return () => {
            alive = false;
        };
    }, [symbol, catalogueFor]);

    /**
     * The shell's first price bars, asked for now rather than once it mounts.
     *
     * The shell mounts only when the catalogue and the saved preference are in
     * (see `ready`), and asks for its bars after that, so the bars were always a
     * second round trip behind the catalogue. They depend on neither, so the
     * request starts here, alongside them, for the window the shell's own first
     * request will ask for (`firstBarsWindow`); the datafeed's bar store then
     * answers the shell from this request, finished or not (#1324).
     *
     * Daily bars, unadjusted: what the shell opens on for a visitor and for
     * anyone without a saved non-daily layout. Until the preference has loaded
     * that is a guess, and a user whose saved layout is weekly or intraday
     * costs one unused request.
     */
    useEffect(() => {
        if (!symbol) return;
        if (prefLoadedRef.current && (prefRef.current.layout?.resolution ?? "1D") !== "1D") return;
        const { from, to } = firstBarsWindow(Date.now(), DAY_MS, INITIAL_BARS);
        void Promise.resolve(datafeedRef.current.getBars({ symbol, resolution: "1D", from, to, countBack: INITIAL_BARS, firstRequest: true })).catch(() => undefined);
    }, [symbol]);

    const pushEvents = useCallback((shell: ChartShellHandle) => {
        const all: ChartEvent[] = [];
        eventsRef.current.forEach((list) => all.push(...list));
        shell.chart.setEvents(all);
    }, []);

    /** One overlay per catalogue series. Flags become events; everything else a series on the scale its axis names. */
    const overlays = useMemo<OverlayDef[]>(() => {
        if (!catalogue || catalogue.symbol !== symbol) return [];
        const seen = new Set<string>();
        const defs: OverlayDef[] = [];
        const initialIds = idsRef.current.get(symbol) ?? null;
        for (const e of catalogue.endpoints) {
            const id = overlayId(e);
            if (seen.has(id) || !e.url) continue;
            seen.add(id);
            const flag = flagOf(e);
            const index = defs.length;
            defs.push({
                id,
                label: e.dataName,
                group: flag ? "Events" : (e.section ?? "Data"),
                // The short-interest legend says which lines start on; the overview catalogue is a picker, price and volume alone start on.
                defaultOn: mode === "shorts" && e.visible === true,
                add: async (ctx) => {
                    const ids = await idsFor(ctx.symbol);
                    const url = retarget(e.url, initialIds?.tradingitemId ?? null, ids.tradingitemId);
                    if (flag) {
                        const from = dayjs().subtract(5, "years").format("YYYY-MM-DD");
                        const to = dayjs().format("YYYY-MM-DD");
                        const rows = ((await getStockShortsFlagsData(url, from, to).catch(() => [])) ?? []) as [number, string | [string, string]][];
                        const tz = ctx.chart.options().timeZone;
                        const events: ChartEvent[] = rows.map(([time, message], k) => ({
                            id: `${id}-${k}`,
                            time: atExchangeMidnight(time, tz),
                            style: "badge",
                            text: flag.label[0],
                            group: flag.label,
                            position: flag.up ? "aboveBar" : "belowBar",
                            tooltip: [flag.label, typeof message === "string" ? message : `${message[0]} ${message[1]}`],
                        }));
                        eventsRef.current.set(id, events);
                        pushEvents(ctx.shell);
                        return () => {
                            eventsRef.current.delete(id);
                            pushEvents(ctx.shell);
                        };
                    }
                    const request = `${url},${e.dataName},${e.dataKey}`;
                    const color = typeof e.color === "string" && e.color && !/^\d+$/.test(e.color) ? e.color : PALETTE[(Number(e.color) || index) % PALETTE.length];
                    const type = e.plotType === "columns" || e.plotType === "histogram" ? "histogram" : e.plotType === "area" ? "area" : "line";
                    // A named axis shares a scale with the other series on it; anything else gets a scale of its own.
                    const scaleId = e.axis && e.axis !== "default" ? `si-${e.axis}` : `own-${id}`;
                    const series: SeriesModel = ctx.chart.addSeries(type, {
                        title: ctx.isCurrentSymbol ? e.dataName : `${e.dataName} · ${ctx.symbol}`,
                        color,
                        lineWidth: 2,
                        priceScaleId: scaleId,
                        zIndex: e.zIndex ?? 1,
                        lastValueVisible: false,
                        priceLineVisible: false,
                        priceFormat: { type: "custom", precision: 2, minMove: 0.01, formatter: (v: number) => (e.millify || Math.abs(v) >= 1e5 ? compact(v) : v.toFixed(2)) },
                    });
                    if (e.yAxisType === 1) ctx.chart.priceScale(scaleId).applyOptions({ mode: "logarithmic" });
                    // The history arrives a window at a time (see `overlayLoading` on the shell) and grows as the user scrolls back.
                    // Points are dated like the bars, or `forwardFill` puts a European or Asian point on the next day's bar.
                    const feed = ctx.windowed(series, async (from, to) => {
                        const tz = ctx.chart.options().timeZone;
                        const points = await fetchSeriesWindow(request, dayjs(from).format("YYYY-MM-DD"), dayjs(to).add(1, "day").format("YYYY-MM-DD"));
                        return points.map((p) => ({ ...p, time: atExchangeMidnight(p.time, tz) }));
                    });
                    return () => {
                        feed.dispose();
                        try {
                            ctx.chart.removeSeries(series.id);
                        } catch {
                            // already gone with a symbol change
                        }
                    };
                },
            });
        }
        // Company events (earnings, conferences, ...) as one overlay per type, off by default.
        if (mode === "shorts" || mode === "overview") {
            defs.push({
                id: "company-events",
                label: "Company events",
                group: "Events",
                defaultOn: false,
                add: async (ctx) => {
                    const ids = await idsFor(ctx.symbol);
                    if (!ids.companyId) return () => {};
                    const rows = await getStockEvents(ids.companyId).catch(() => []);
                    const tz = ctx.chart.options().timeZone;
                    const events: ChartEvent[] = rows.map((r, k): ChartEvent => ({
                        id: `company-${k}`,
                        time: atExchangeMidnight(r.x, tz),
                        style: "lane",
                        text: r.title,
                        group: r.type,
                        tooltip: [
                            r.type,
                            r.text
                                .replace(/<[^>]+>/g, " ")
                                .replace(/\s+/g, " ")
                                .trim(),
                        ],
                    }));
                    eventsRef.current.set("company-events", events);
                    pushEvents(ctx.shell);
                    return () => {
                        eventsRef.current.delete("company-events");
                        pushEvents(ctx.shell);
                    };
                },
            });
        }
        return defs;
    }, [catalogue, symbol, mode, idsFor, pushEvents]);

    const applyVolume = useCallback((shell: ChartShellHandle, on: boolean) => {
        const vol = shell.chart.volume;
        if (!vol) return;
        vol.applyOptions({ visible: on });
        if (vol.options.pane) shell.chart.chart.setPaneCollapsed(vol.options.pane, !on);
    }, []);
    const applyTemplate = useCallback(
        (t: ChartTemplate) => {
            const shell = shellRef.current;
            setPref({ layout: t.layout, volume: t.volume });
            if (shell) {
                void shell.setLayout(t.layout);
                applyVolume(shell, t.volume !== false);
            }
            setSnack({ open: true, text: `Template "${t.name}" applied. Save it as your default if you want it on every page of this kind.`, severity: "success" });
        },
        [setPref, applyVolume]
    );

    const [shareDialog, setShareDialog] = useState<{ open: boolean; loading: boolean; url: string; imageUrl: string }>({ open: false, loading: false, url: "", imageUrl: "" });
    /**
     * A shareable branded snapshot of this chart (ortex/web#1675): the shareable
     * short-interest lines that are on and the visible date range go to the
     * platform, which snapshots the data, renders the card and returns a link.
     */
    const shareChart = useCallback(async () => {
        const shell = shellRef.current;
        const ids = await idsFor(symbol).catch(() => null);
        if (!shell || !ids?.tradingitemId) return;
        setShareDialog({ open: true, loading: true, url: "", imageUrl: "" });
        try {
            const on = new Set(shell.getLayout().overlays ?? []);
            const keys = (catalogue?.endpoints ?? [])
                .filter((e) => on.has(overlayId(e)) && e.url)
                .map((e) => measureKeyFromUrl(e.url))
                .filter((key): key is string => !!key && SHAREABLE_MEASURES.has(key));
            const measures = Array.from(new Set(keys)).slice(0, 3);
            if (!measures.length) measures.push("sie");
            const range = shell.chart.chart.timeScale.visibleRange();
            const fromDate = range.fromTime > 0 ? dayjs(range.fromTime) : dayjs().subtract(90, "day");
            const toDate = range.toTime > 0 && dayjs(range.toTime).isBefore(dayjs()) ? dayjs(range.toTime) : dayjs();
            const res = await createSharedChart({ tradingitem_id: ids.tradingitemId, measures, from_date: fromDate.format("YYYY-MM-DD"), to_date: toDate.format("YYYY-MM-DD") });
            if (res.status === 201 && res.data?.slug) {
                setShareDialog({ open: true, loading: false, url: res.data.url, imageUrl: res.data.imageUrl || "" });
                trackMIEvent("shared_chart_created", { ticker: stock?.ticker, slug: res.data.slug, measures: measures.join(",") });
            } else {
                setShareDialog({ open: false, loading: false, url: "", imageUrl: "" });
                const needsAccount = res.status === 401 || res.status === 403;
                setSnack({
                    open: true,
                    text: needsAccount ? "Sign in to share charts." : "Could not create the share link. Please try again.",
                    severity: needsAccount ? "info" : "error",
                    action: needsAccount && shareSignup.isVisitor ? { label: "Sign up", onClick: shareSignup.onClick } : undefined,
                });
            }
        } catch (error) {
            console.error("[StockChart] share failed", error);
            setShareDialog({ open: false, loading: false, url: "", imageUrl: "" });
            setSnack({ open: true, text: "Could not create the share link. Please try again.", severity: "error" });
        }
    }, [symbol, catalogue, stock?.ticker, idsFor, shareSignup.isVisitor, shareSignup.onClick]);
    useEffect(() => {
        if (shareHandlerRef) shareHandlerRef.current = () => void shareChart();
    }, [shareHandlerRef, shareChart]);

    const actions = useMemo<ToolbarAction[]>(
        () => [
            {
                id: "adj",
                label: "ADJ",
                title: adjusted ? "Showing dividend-adjusted prices" : "Adjust prices for dividends",
                placement: "middle",
                active: adjusted,
                onClick: (shell) => {
                    const on = !adjustedRef.current;
                    adjustedRef.current = on;
                    setAdjusted(on);
                    // The shell reads these definitions once, when it is built, so the
                    // button is told directly. Without this it never showed whether
                    // adjusted prices were on.
                    shell.setAction("adj", { active: on, title: on ? "Showing dividend-adjusted prices" : "Adjust prices for dividends" });
                    // The datafeed reads the flag on every bar request; reload fetches
                    // the same symbol again and keeps the reader's place.
                    void shell.binding?.reload();
                },
            },
            {
                id: "volume",
                icon: "bars",
                title: "Show or hide the volume pane",
                placement: "middle",
                onClick: (shell) => {
                    const next = !prefRef.current.volume;
                    applyVolume(shell, next);
                    setPref({ volume: next });
                },
            },
            ...(mode === "shorts" ? [{ id: "share", icon: "share" as const, title: "Share a snapshot of this chart", placement: "end" as const, onClick: () => void shareChart() }] : []),
            {
                id: "export",
                icon: "download",
                title: "Export",
                placement: "end",
                menu: [{ label: "Download Data API", hint: "docs.ortex.com", onSelect: () => window.open("https://docs.ortex.com", "_blank", "noopener") }],
            },
            {
                id: "save",
                icon: SAVE_ICON,
                title: "Save this chart",
                placement: "end",
                menu: [
                    { label: "Save this chart", hint: `For ${symbol} only`, onSelect: (shell) => saveForStock(shell) },
                    { label: `Use as default on ${pageKind} pages`, hint: "Every stock's page of this kind", onSelect: (shell) => saveAsDefault(shell) },
                    { label: "Save as template…", hint: "Name it, use it anywhere", onSelect: () => setTemplateDialog({ open: true, name: "", mode: "save" }) },
                    { label: "Load template…", hint: templates.length ? `${templates.length} saved` : "None saved yet", onSelect: () => setTemplateDialog({ open: true, name: "", mode: "load" }) },
                    {
                        label: "Reset to the ORTEX default",
                        onSelect: (shell) => {
                            resetPref();
                            void shell.setLayout(null);
                            applyVolume(shell, true);
                            setSnack({ open: true, text: "Chart reset to the ORTEX default.", severity: "success" });
                        },
                    },
                ],
            },
            { id: "help", icon: "note", title: "What the buttons do", placement: "end", onClick: () => setHelpOpen(true) },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [applyVolume, resetPref, setPref, symbol, adjusted, mode, shareChart, saveForStock, saveAsDefault, pageKind, templates.length, applyTemplate]
    );

    const shellOptions = useMemo(
        () => ({
            datafeed,
            resolutions: ORTEX_RESOLUTIONS,
            // The periods a reader asks for, each with the resolution that makes it
            // readable: a day of daily bars is one candle, and five years of minutes
            // is nobody's intention. The intraday feed holds about a week, so the day
            // and the week are the only two that go below a daily bar.
            ranges: [
                { label: "1D", ms: 86_400_000, resolution: "5", title: "The last day, in five-minute bars" },
                { label: "1W", ms: 7 * 86_400_000, resolution: "60", title: "The last week, in hourly bars" },
                { label: "1M", ms: 30 * 86_400_000, resolution: "1D", title: "The last month" },
                { label: "1Q", ms: 91 * 86_400_000, resolution: "1D", title: "The last quarter" },
                { label: "1Y", ms: 365 * 86_400_000, resolution: "1D", title: "The last year" },
                { label: "5Y", ms: 5 * 365 * 86_400_000, resolution: "1W", title: "The last five years, in weekly bars" },
                { label: "10Y", ms: 10 * 365 * 86_400_000, resolution: "1W", title: "The last ten years, in weekly bars" },
            ],
            initialBars: INITIAL_BARS,
            overlays,
            dataPicker: "modal" as const,
            compare: true,
            download: { fileName: `${symbol.replace(":", "-")}-chart.csv` },
            actions,
            screenshotFileName: `${symbol.replace(":", "-")}-chart.png`,
            // The ORTEX mark stays on our own charts; the OEM licence would let us hide it.
            branding: { visible: true, height: 30, opacity: 0.75 },
            // The ORTEX wordmark faint behind the price: our own product, enabled by configuration.
            watermark: { visible: true, logo: "ortex", opacity: 0.06, logoScale: 0.125 },
            // Overlays fetch the screen plus half a screen either side, then more as the user scrolls back.
            // The first request goes out before the price bars have arrived, when the chart cannot yet say
            // what the screen will show, so it is sized to cover the year a daily chart opens on. The
            // library's default of ninety days fell short of that on every load, and each line was
            // fetched and drawn a second time the moment the bars set the view.
            overlayLoading: { margin: 0.5, earliest: Date.UTC(2005, 0, 1), initialSpan: 365 * 86_400_000 },
            // The number beside the cursor, for the line the cursor is on. Not
            // every line: a stock page carries a dozen, and a box listing all of
            // them answers a question nobody asked. The legend still has them all.
            tooltip: {
                mode: "hovered" as const,
                // The changes go on the quieter second line, under the value.
                template: "{name}  {value}\n1d {value % value[-1]}   1w {value % value[-7]}",
            },
            // The company logo in the legend added nothing beside the ticker and crowded the collapse arrow.
            legendLogo: false,
            toolbar: { symbol: !onStockPage },
            // The chart is the page here, so the furniture starts out of the
            // way: the drawing tools as a thumb, and the view and export
            // controls behind one button. Both are starting positions, and a
            // reader who opens either keeps it open.
            drawingRailCollapsed: true,
            extrasCollapsed: true,
            volume: prefRef.current.volume !== false,
            onLayoutChange: (layout: ChartLayout) => setPref({ layout }),
            onError: (err: unknown) => console.error("[StockChart]", err),
        }),
        // Overlays and actions are the only inputs that change what the shell can do; the rest is fixed for the mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [overlays, actions, datafeed, onStockPage]
    );

    const handleReady = useCallback(
        (shell: ChartShellHandle) => {
            shellRef.current = shell;
            if (prefRef.current.volume === false) applyVolume(shell, false);
            onReady?.(shell);
        },
        [applyVolume, onReady]
    );

    return (
        <div className={[styles.root, className ?? ""].join(" ")} style={{ height: height ?? "100%", ...style }}>
            {mode === "shorts" && !advanced && (
                <Typography variant="body2" className={styles.notice} sx={{ color: grey }}>
                    Access to some data is restricted for this account tier. Full data, including history older than 9 months and the most up-to-date information, is only available with an ORTEX Advanced Subscription.{" "}
                    <Box
                        component="button"
                        type="button"
                        onClick={shortsUnlock.onClick}
                        disabled={shortsUnlock.loading}
                        sx={{ background: "none", border: 0, p: 0, font: "inherit", color: "var(--accent-text, #31aba6)", textDecoration: "underline", cursor: "pointer" }}
                    >
                        {shortsUnlock.label}
                    </Box>
                </Typography>
            )}
            <div className={styles.shell}>
                {ready && prefMeta.loaded ? (
                    <ChartShell key={`${symbol}-${mode}`} options={shellOptions} symbol={symbol} resolution={pref.layout?.resolution ?? "1D"} theme={theme} layout={pref.layout ?? null} onReady={handleReady} className={styles.canvas} />
                ) : (
                    <div className={styles.loading} />
                )}
            </div>
            <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Chart buttons explained</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {[
                            { name: "Data", desc: "Add or remove ORTEX series on the chart, grouped by category, or compare another symbol." },
                            { name: "Indicators", desc: "Technical studies from the built-in library, with settings per study." },
                            { name: "Legend ⋯", desc: "Move a series to the right, left or its own scale, to the main pane or a new pane, hide it or remove it." },
                            { name: "Volume", desc: "Toggle the volume pane at the bottom of the chart. Your choice is remembered." },
                            { name: "Download", desc: "Get this chart's data, and much more, over the ORTEX Data API." },
                            { name: "Save", desc: "Save this chart for this stock only, make it your default on every page of this kind, save it as a named template you can apply anywhere, or go back to the ORTEX default." },
                            { name: "Legend ⋯ Colour", desc: "Pick a colour for any line, or up and down colours for candles. The first line of that kind always gets your colour; a second copy gets a contrasting one." },
                            { name: "Remembered for you", desc: "Changes you make stay with you while you browse. Save keeps them: for one stock, or as your default for every page of this kind, on every device you sign in on." },
                        ].map((item) => (
                            <div key={item.name}>
                                <Typography sx={{ color: textColor, fontWeight: 600 }}>{item.name}</Typography>
                                <Typography variant="body2" sx={{ color: grey }}>
                                    {item.desc}
                                </Typography>
                            </div>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHelpOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={templateDialog.open} onClose={() => setTemplateDialog((d) => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
                <DialogTitle>{templateDialog.mode === "load" ? "Load a template" : "Save as template"}</DialogTitle>
                <DialogContent>
                    {templateDialog.mode === "save" && (
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            label="Template name"
                            value={templateDialog.name}
                            onChange={(e) => setTemplateDialog((d) => ({ ...d, name: e.target.value }))}
                            onKeyDown={(e) => e.key === "Enter" && saveTemplate(templateDialog.name)}
                            sx={{ mt: 1 }}
                        />
                    )}
                    {templates.length === 0 && templateDialog.mode === "load" && (
                        <Typography variant="body2" sx={{ color: grey, mt: 1 }}>
                            No templates yet. Set the chart up the way you like it and choose Save as template.
                        </Typography>
                    )}
                    {templates.length > 0 && (
                        <Stack spacing={0.5} sx={{ mt: 2 }}>
                            <Typography variant="caption" sx={{ color: grey }}>
                                Your templates
                            </Typography>
                            {templates.map((t) => (
                                <Box key={t.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography variant="body2" sx={{ flex: 1, color: textColor }}>
                                        {t.name}
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant={templateDialog.mode === "load" ? "contained" : "text"}
                                        onClick={() => {
                                            applyTemplate(t);
                                            setTemplateDialog((d) => ({ ...d, open: false }));
                                        }}
                                    >
                                        Apply
                                    </Button>
                                    <Button size="small" onClick={() => setTemplateDialog({ open: true, name: t.name, mode: "save" })}>
                                        Overwrite
                                    </Button>
                                    <Button
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                            savePreference(t.key, null);
                                            setTemplateDialog((d) => ({ ...d }));
                                        }}
                                    >
                                        Delete
                                    </Button>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTemplateDialog((d) => ({ ...d, open: false }))}>{templateDialog.mode === "load" ? "Close" : "Cancel"}</Button>
                    {templateDialog.mode === "save" && (
                        <Button variant="contained" disabled={!templateDialog.name.trim()} onClick={() => saveTemplate(templateDialog.name)}>
                            Save
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            <Dialog open={shareDialog.open} onClose={() => setShareDialog((d) => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
                <DialogTitle>Share this chart</DialogTitle>
                <DialogContent>
                    {shareDialog.loading ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2">Creating the snapshot…</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {shareDialog.imageUrl && <Box component="img" src={shareDialog.imageUrl} alt="Chart snapshot" sx={{ width: "100%", borderRadius: 1 }} />}
                            <TextField
                                size="small"
                                fullWidth
                                value={shareDialog.url}
                                onFocus={(e) => e.target.select()}
                                slotProps={{
                                    input: { readOnly: true },
                                }}
                            />
                            <Typography variant="caption">The card is a date-stamped snapshot of ORTEX data at share time. Anyone with the link can view it.</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShareDialog((d) => ({ ...d, open: false }))}>Close</Button>
                    {!shareDialog.loading && shareDialog.url && (
                        <Button
                            variant="contained"
                            onClick={() => {
                                void navigator.clipboard?.writeText(shareDialog.url);
                                setSnack({ open: true, text: "Link copied.", severity: "success" });
                            }}
                        >
                            Copy link
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            <SnackBar open={snack.open} setOpen={(open) => setSnack({ ...snack, open })} severity={snack.severity} text={snack.text} action={snack.action} />
        </div>
    );
};

function compact(v: number): string {
    const a = Math.abs(v);
    if (a >= 1e12) return `${(v / 1e12).toFixed(2)}T`;
    if (a >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
    if (a >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
    if (a >= 1e3) return `${(v / 1e3).toFixed(2)}K`;
    return v.toFixed(2);
}

export default StockChart;
